/**
 * IndexedDB persistence (spec §8.13): Loro snapshots, pending local updates, upload queue,
 * query cache. Uses `idb` for a thin promise wrapper.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

interface KssDB extends DBSchema {
  loroSnapshots: { key: string; value: { id: string; snapshot: Uint8Array; savedAt: number } };
  loroPending: { key: number; value: { id: number; docKey: string; update: Uint8Array; createdAt: number }; indexes: { byDoc: string } };
  uploads: { key: string; value: { hash: string; name: string; mime: string; size: number; state: "queued" | "uploading" | "done" | "failed"; error?: string } };
  queryCache: { key: string; value: { key: string; result: unknown; fetchedAt: number } };
  meta: { key: string; value: { key: string; value: unknown } };
}

let dbPromise: Promise<IDBPDatabase<KssDB>> | null = null;

export function db(): Promise<IDBPDatabase<KssDB>> {
  if (!dbPromise) {
    dbPromise = openDB<KssDB>("kss", 1, {
      upgrade(d) {
        d.createObjectStore("loroSnapshots", { keyPath: "id" });
        const pending = d.createObjectStore("loroPending", { keyPath: "id", autoIncrement: true });
        pending.createIndex("byDoc", "docKey");
        d.createObjectStore("uploads", { keyPath: "hash" });
        d.createObjectStore("queryCache", { keyPath: "key" });
        d.createObjectStore("meta", { keyPath: "key" });
      },
    });
  }
  return dbPromise;
}

export async function loadSnapshot(docKey: string): Promise<Uint8Array | undefined> {
  return (await (await db()).get("loroSnapshots", docKey))?.snapshot;
}

export async function saveSnapshot(docKey: string, snapshot: Uint8Array): Promise<void> {
  await (await db()).put("loroSnapshots", { id: docKey, snapshot, savedAt: Date.now() });
}

// --- pending local updates ------------------------------------------------------------------------
//
// IndexedDB writes are asynchronous and a page unload aborts the ones still in flight, so an edit made
// in the instant before a reload or a closed tab could be lost (M12 finding). Updates whose IndexedDB
// write has not completed are therefore kept in memory, and on `pagehide` — or when the page is hidden,
// the last event a mobile browser reliably delivers — they are written *synchronously* to a small
// localStorage journal. The next load reads the journal together with IndexedDB and moves it into
// IndexedDB. An update present in both is harmless: Loro ignores updates it already holds.

const JOURNAL_PREFIX = "kss:pending-journal:";

interface JournalEntry {
  ts: number;
  /** base64 of the Loro update */
  u: string;
}

const inFlight = new Map<string, Set<{ ts: number; update: Uint8Array }>>();

export async function appendPending(docKey: string, update: Uint8Array): Promise<void> {
  const entry = { ts: Date.now(), update };
  let set = inFlight.get(docKey);
  if (!set) inFlight.set(docKey, (set = new Set()));
  set.add(entry);
  try {
    await (await db()).add("loroPending", { docKey, update, createdAt: entry.ts } as never);
    set.delete(entry);
  } catch (e) {
    // stays in flight: the unload flush journals it
    console.warn("pending update not persisted to IndexedDB", e);
  }
}

export function toBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

export function fromBase64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function readJournal(docKey: string): JournalEntry[] {
  try {
    const raw = localStorage.getItem(JOURNAL_PREFIX + docKey);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as JournalEntry[]).filter((e) => typeof e?.u === "string" && typeof e?.ts === "number") : [];
  } catch {
    return [];
  }
}

/** Synchronously journal every update whose IndexedDB write has not completed. Safe to call repeatedly. */
export function flushPendingSync(): number {
  let written = 0;
  for (const [docKey, entries] of inFlight) {
    if (!entries.size) continue;
    const journal = readJournal(docKey);
    const known = new Set(journal.map((e) => `${e.ts}:${e.u}`));
    for (const e of entries) {
      const item = { ts: e.ts, u: toBase64(e.update) };
      if (known.has(`${item.ts}:${item.u}`)) continue;
      journal.push(item);
      written++;
    }
    try {
      localStorage.setItem(JOURNAL_PREFIX + docKey, JSON.stringify(journal));
    } catch (e) {
      console.warn("pending journal not written", e); // quota; nothing more can be done at unload
    }
  }
  return written;
}

let flushInstalled = false;

/** Registers the unload flush once per page (called by the doc manager). */
export function installPendingFlush(): void {
  if (flushInstalled || typeof window === "undefined") return;
  flushInstalled = true;
  window.addEventListener("pagehide", () => void flushPendingSync());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPendingSync();
  });
}

export async function loadPending(docKey: string): Promise<Uint8Array[]> {
  const d = await db();
  const rows = await d.getAllFromIndex("loroPending", "byDoc", docKey);
  const journal = readJournal(docKey);
  if (journal.length) {
    // move the journal into IndexedDB, then drop it
    const tx = d.transaction("loroPending", "readwrite");
    for (const e of journal) void tx.store.add({ docKey, update: fromBase64(e.u), createdAt: e.ts } as never);
    await tx.done;
    localStorage.removeItem(JOURNAL_PREFIX + docKey);
  }
  return [...rows.map((r) => r.update), ...journal.map((e) => fromBase64(e.u))];
}

export async function clearPending(docKey: string): Promise<void> {
  const d = await db();
  const tx = d.transaction("loroPending", "readwrite");
  let cursor = await tx.store.index("byDoc").openCursor(docKey);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
  // the server has everything the snapshot holds: journal entries are no longer needed
  try {
    localStorage.removeItem(JOURNAL_PREFIX + docKey);
  } catch {
    /* storage unavailable */
  }
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await (await db()).get("meta", key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put("meta", { key, value });
}
